import React, { useEffect, useState } from 'react'
import { Popover } from '@ui-kitten/components'
import moment from 'moment'
import { observer } from 'mobx-react'
import {
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from 'react-native'
import Color from 'color'
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons'
import { BtInput, BtInputProps } from './BtInput'
import { BtText } from './BtText'
import { colors } from '../theme'
import { BtButton } from './BtButton'
import { Spacer } from './Spacer'
import { createEditable } from '../helpers'
import { KeyboardType } from '../../../models/src/dictionaries'

const months = [
  { '01': 'Jan' },
  { '02': 'Feb' },
  { '03': 'Mar' },
  { '04': 'Apr' },
  { '05': 'May' },
  { '06': 'Jun' },
  { '07': 'Jul' },
  { '08': 'Aug' },
  { '09': 'Sep' },
  { '10': 'Oct' },
  { '11': 'Nov' },
  { '12': 'Dec' },
]

const ArrowButtons = ({ dir = 'right', onPress }) => (
  <TouchableOpacity
    onPress={onPress}
    style={{
      width: 30,
      height: 30,
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: 3,
    }}
  >
    <MaterialCommunityIcons
      name={dir === 'right' ? 'chevron-right' : 'chevron-left'}
      size={25}
      color={colors.primary}
    />
  </TouchableOpacity>
)

const MonthItem = ({ month, active, onPress }) => (
  <TouchableOpacity
    style={{
      width: 76,
      height: 44,
      borderRadius: 4,
      justifyContent: 'center',
      alignItems: 'center',
      marginTop: 0,
      borderWidth: active ? 0.5 : 0,
      borderColor: colors.borderColor,
      backgroundColor: active
        ? Color(colors.accentText).alpha(0.1).rgb().string()
        : 'white',
    }}
    onPress={onPress}
  >
    <BtText size={15} weight={'400'}>
      {month}
    </BtText>
  </TouchableOpacity>
)

const YearRow = ({ value, onChange, onArrowClick }) => {
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        paddingHorizontal: 10,
      }}
    >
      <TextInput
        value={value}
        keyboardType={KeyboardType.Numeric}
        maxLength={4}
        style={{
          width: 70,
          fontSize: 18,
          fontWeight: '600',
          textAlign: 'center',
          padding: 4,
          borderWidth: 1,
          borderColor: colors.borderColor,
          borderRadius: 4,
        }}
        onChangeText={onChange}
      />
      <View
        style={{
          flexDirection: 'row',
          width: 65,
          justifyContent: 'space-between',
        }}
      >
        <ArrowButtons onPress={() => onArrowClick(-1)} dir={'left'} />
        <ArrowButtons onPress={() => onArrowClick(1)} />
      </View>
    </View>
  )
}

interface MonthPickerProps
  extends Omit<BtInputProps, 'onChangeText' | 'onChange'> {
  onChange?: (value: string) => void
  minYear?: string | number
  maxYear?: string | number
}

// mm/yyyy or m/yyyy match
function matchesMonthAndYear(input) {
  return /^((0?[1-9]|1[0-2])\/\d{4})$/.test(input)
}

export const BtMonthYearPicker_ = observer(
  ({
    errorText = '',
    label,
    mandatory = false,
    value,
    onChange,
    minYear = 1900,
    maxYear = 2100,
    ...rest
  }: MonthPickerProps) => {
    const today = moment()
    const [visible, setVisible] = useState(false)
    const [currentMonth, setMonth] = useState(today.format('MM'))
    const [currentYear, setYear] = useState(today.format('YYYY'))

    // expected format MM/YYYY
    useEffect(() => {
      if (value && matchesMonthAndYear(value)) {
        const monthYear = value.split('/')
        const cMonth =
          monthYear[0].length === 1 ? '0' + monthYear[0] : monthYear[0]
        setMonth(cMonth)
        setYear(monthYear[1])
      } else {
        setMonth(today.format('MM'))
        setYear(today.format('YYYY'))
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const validYear = (year) => {
      if (!year) return false
      const theYear = parseInt(year)

      if (String(theYear).length !== 4) return false
      if (
        (typeof minYear === 'string' ? parseInt(minYear) : minYear) >
        parseInt(year)
      )
        return false
      return (
        (typeof maxYear === 'string' ? parseInt(maxYear) : maxYear) >=
        parseInt(year)
      )
    }

    const updateYear = (step) => {
      let year = parseInt(currentYear)
      if (!year || isNaN(year)) {
        year = parseInt(today.format('YYYY'))
      }
      const newYear = year + step
      if (validYear(newYear)) {
        setYear(String(newYear))
      }
    }
    const disabled = !validYear(currentYear)

    const renderAnchor = () => (
      <TouchableWithoutFeedback onPress={() => setVisible(true)}>
        <BtInput
          errorText={errorText}
          value={value}
          label={label}
          mandatory={mandatory}
          editable={false}
          onClear={() => onChange && onChange('')}
          {...rest}
        />
      </TouchableWithoutFeedback>
    )

    const onConfirm = () => {
      const cValue = `${currentMonth}/${currentYear}`
      onChange && onChange(cValue)
      setVisible(false)
    }

    const onChangeText = (year) => {
      setYear(year)
    }

    return (
      <Popover
        visible={visible}
        anchor={renderAnchor}
        onBackdropPress={() => setVisible(false)}
      >
        <View
          style={{
            padding: 10,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <YearRow
            onArrowClick={updateYear}
            value={currentYear}
            onChange={onChangeText}
          />
          <View
            style={{
              flexDirection: 'row',
              justifyContent: 'space-between',
              width: 305,
              flexWrap: 'wrap',
              marginTop: 10,
              paddingTop: 10,
              borderTopColor: colors.borderColor,
              borderBottomColor: colors.borderColor,
              borderTopWidth: 1,
              borderBottomWidth: 1,
              marginBottom: 10,
              paddingBottom: 10,
            }}
          >
            {months.map((month) => {
              const m = Object.entries(month)[0]
              const key = m[0],
                name = m[1]
              return (
                <MonthItem
                  month={name}
                  onPress={() => setMonth(key)}
                  active={key === currentMonth}
                  key={key}
                />
              )
            })}
          </View>
          <View
            style={{
              flexDirection: 'row-reverse',
              width: '100%',
              paddingHorizontal: 15,
            }}
          >
            <BtButton
              style={{ width: 80 }}
              onPress={onConfirm}
              disabled={disabled}
              size="small"
            >
              Select
            </BtButton>
            <Spacer width={10} height={10} />
            <BtButton
              appearance="ghost"
              status="basic"
              size="small"
              onPress={() => setVisible(false)}
            >
              Cancel
            </BtButton>
          </View>
        </View>
      </Popover>
    )
  },
)

export const BtMonthYearPicker = createEditable({
  Component: BtMonthYearPicker_,
  valuePropertyName: 'value',
  changePropertyName: 'onChange',
})
